import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Idea, IdeaCreate, IdeasByCategory, FeatureCategory, Feature, IdeaType, IdeaStatus, AITeamMember } from '../types';
import {
  getIdeasByCategory,
  createIdea,
  updateIdea,
  deleteIdea,
  acceptIdeaAsFeature,
  acceptIdeaAsTask,
  rejectIdea,
  deferIdea,
  getFeatures,
  getTeamMembers,
} from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useTaskModal } from '../context/TaskModalContext';

// Status badge colors
const getStatusColor = (status: IdeaStatus) => {
  switch (status) {
    case IdeaStatus.PROPOSED: return '#f59e0b';
    case IdeaStatus.ACCEPTED: return '#10b981';
    case IdeaStatus.REJECTED: return '#ef4444';
    case IdeaStatus.DEFERRED: return '#6b7280';
    default: return '#6b7280';
  }
};

// Idea type badge colors
const getTypeColor = (type: IdeaType) => {
  switch (type) {
    case IdeaType.NEW_FEATURE: return '#8b5cf6';
    case IdeaType.FEATURE_ENHANCEMENT: return '#3b82f6';
    default: return '#6b7280';
  }
};

// Idea Card Component
const IdeaCard: React.FC<{
  idea: Idea;
  onClick: () => void;
}> = ({ idea, onClick }) => {
  const { theme } = useTheme();

  return (
    <div
      onClick={onClick}
      style={{
        padding: '16px',
        backgroundColor: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        marginBottom: '8px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.primary;
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.transform = 'translateX(0)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, color: theme.text, fontSize: '14px', fontWeight: '600' }}>
          {idea.title}
        </h4>
        <div style={{ display: 'flex', gap: '4px' }}>
          <span style={{
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '500',
            backgroundColor: `${getTypeColor(idea.idea_type)}20`,
            color: getTypeColor(idea.idea_type),
          }}>
            {idea.idea_type === IdeaType.NEW_FEATURE ? 'New Feature' : 'Enhancement'}
          </span>
          <span style={{
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '500',
            backgroundColor: `${getStatusColor(idea.status)}20`,
            color: getStatusColor(idea.status),
          }}>
            {idea.status}
          </span>
        </div>
      </div>
      <p style={{ margin: 0, color: theme.textSecondary, fontSize: '12px', lineHeight: '1.4' }}>
        {idea.description.length > 150 ? idea.description.substring(0, 150) + '...' : idea.description}
      </p>
      <div style={{ marginTop: '8px', fontSize: '11px', color: theme.textSecondary }}>
        By {idea.created_by} • {new Date(idea.created_at).toLocaleDateString()}
        {idea.feature_id && <span> • For: {idea.feature_id}</span>}
      </div>
    </div>
  );
};

// Create Idea Modal
const CreateIdeaModal: React.FC<{
  categories: FeatureCategory[];
  features: Feature[];
  onClose: () => void;
  onSave: (idea: IdeaCreate) => void;
}> = ({ categories, features, onClose, onSave }) => {
  const { theme } = useTheme();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ideaType, setIdeaType] = useState<IdeaType>(IdeaType.NEW_FEATURE);
  const [category, setCategory] = useState(categories[0]?.id || '');
  const [featureId, setFeatureId] = useState<string>('');
  const [createdBy, setCreatedBy] = useState(localStorage.getItem('ai_feedback_name') || '');
  const [aiTeamMember, setAiTeamMember] = useState<string>('');
  const [teamMembers, setTeamMembers] = useState<AITeamMember[]>([]);

  useEffect(() => {
    getTeamMembers().then(setTeamMembers).catch(console.error);
  }, []);

  const handleSubmit = () => {
    if (!title.trim() || !description.trim() || !category) return;
    if (ideaType === IdeaType.FEATURE_ENHANCEMENT && !featureId) return;

    // Save name for future use
    localStorage.setItem('ai_feedback_name', createdBy);

    onSave({
      title: title.trim(),
      description: description.trim(),
      idea_type: ideaType,
      category,
      feature_id: ideaType === IdeaType.FEATURE_ENHANCEMENT ? featureId : undefined,
      created_by: createdBy,
      ai_team_member: aiTeamMember || undefined,
    });
  };

  // Filter features by selected category
  const categoryFeatures = features.filter(f => f.category === category);

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '16px',
    }}>
      <div style={{
        backgroundColor: theme.surface,
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        <h2 style={{ margin: '0 0 20px', color: theme.text }}>New Idea</h2>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: theme.text, fontWeight: '500' }}>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief title for the idea"
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              backgroundColor: theme.background,
              color: theme.text,
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: theme.text, fontWeight: '500' }}>Type *</label>
          <select
            value={ideaType}
            onChange={(e) => {
              setIdeaType(e.target.value as IdeaType);
              setFeatureId('');
            }}
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              backgroundColor: theme.background,
              color: theme.text,
            }}
          >
            <option value={IdeaType.NEW_FEATURE}>New Feature</option>
            <option value={IdeaType.FEATURE_ENHANCEMENT}>Enhancement to Existing Feature</option>
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: theme.text, fontWeight: '500' }}>Category *</label>
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setFeatureId('');
            }}
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              backgroundColor: theme.background,
              color: theme.text,
            }}
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {ideaType === IdeaType.FEATURE_ENHANCEMENT && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: theme.text, fontWeight: '500' }}>Feature to Enhance *</label>
            <select
              value={featureId}
              onChange={(e) => setFeatureId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                backgroundColor: theme.background,
                color: theme.text,
              }}
            >
              <option value="">Select a feature...</option>
              {categoryFeatures.map(feature => (
                <option key={feature.id} value={feature.id}>{feature.name}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: theme.text, fontWeight: '500' }}>Description *</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Detailed description of the idea, including what it should accomplish..."
            style={{
              width: '100%',
              minHeight: '150px',
              padding: '10px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              backgroundColor: theme.background,
              color: theme.text,
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: theme.text, fontWeight: '500' }}>Suggested Team Member</label>
          <select
            value={aiTeamMember}
            onChange={(e) => setAiTeamMember(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              backgroundColor: theme.background,
              color: theme.text,
            }}
          >
            <option value="">Not specified</option>
            {teamMembers.map(member => (
              <option key={member.name} value={member.name}>{member.name} - {member.role}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: theme.text, fontWeight: '500' }}>Your Name *</label>
          <input
            type="text"
            value={createdBy}
            onChange={(e) => setCreatedBy(e.target.value)}
            placeholder="Your name"
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              backgroundColor: theme.background,
              color: theme.text,
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              backgroundColor: theme.surface,
              color: theme.text,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || !description.trim() || !category || !createdBy.trim() ||
              (ideaType === IdeaType.FEATURE_ENHANCEMENT && !featureId)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: theme.primary,
              color: '#fff',
              cursor: 'pointer',
              opacity: (!title.trim() || !description.trim() || !category || !createdBy.trim() ||
                (ideaType === IdeaType.FEATURE_ENHANCEMENT && !featureId)) ? 0.5 : 1,
            }}
          >
            Create Idea
          </button>
        </div>
      </div>
    </div>
  );
};

// Idea Detail Modal with Accept/Reject actions
const IdeaDetailModal: React.FC<{
  idea: Idea;
  features: Feature[];
  categories: FeatureCategory[];
  onClose: () => void;
  onAcceptAsFeature: (expectations?: string) => void;
  onAcceptAsTask: (aiTeamMember: string, featureId?: string) => void;
  onReject: () => void;
  onDefer: () => void;
  onDelete: () => void;
  onUpdate: (updates: { title?: string; description?: string; category?: string; feature_id?: string; ai_team_member?: string }) => void;
}> = ({ idea, features, categories, onClose, onAcceptAsFeature, onAcceptAsTask, onReject, onDefer, onDelete, onUpdate }) => {
  const { theme } = useTheme();
  const { openTask } = useTaskModal();
  const [showAcceptTaskForm, setShowAcceptTaskForm] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState(idea.ai_team_member || '');
  const [selectedFeatureId, setSelectedFeatureId] = useState(idea.feature_id || '');
  const [teamMembers, setTeamMembers] = useState<AITeamMember[]>([]);
  const [expectations, setExpectations] = useState(idea.description);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(idea.title);
  const [editDescription, setEditDescription] = useState(idea.description);
  const [editCategory, setEditCategory] = useState(idea.category);
  const [editFeatureId, setEditFeatureId] = useState(idea.feature_id || '');
  const [editAiTeamMember, setEditAiTeamMember] = useState(idea.ai_team_member || '');

  useEffect(() => {
    getTeamMembers().then(setTeamMembers).catch(console.error);
  }, []);

  const isProposed = idea.status === IdeaStatus.PROPOSED;

  // Filter features by selected edit category
  const categoryFeatures = features.filter(f => f.category === editCategory);

  const handleSaveEdit = () => {
    onUpdate({
      title: editTitle,
      description: editDescription,
      category: editCategory,
      feature_id: idea.idea_type === IdeaType.FEATURE_ENHANCEMENT ? editFeatureId : undefined,
      ai_team_member: editAiTeamMember || undefined,
    });
    setIsEditing(false);
    setExpectations(editDescription); // Update expectations for Accept as Feature
  };

  const handleCancelEdit = () => {
    setEditTitle(idea.title);
    setEditDescription(idea.description);
    setEditCategory(idea.category);
    setEditFeatureId(idea.feature_id || '');
    setEditAiTeamMember(idea.ai_team_member || '');
    setIsEditing(false);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '16px',
    }}>
      <div style={{
        backgroundColor: theme.surface,
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {isEditing ? (
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '18px',
                  fontWeight: '600',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  backgroundColor: theme.background,
                  color: theme.text,
                  marginBottom: '8px',
                }}
              />
            ) : (
              <h2 style={{ margin: '0 0 8px', color: theme.text, wordBreak: 'break-word' }}>{idea.title}</h2>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: `${getTypeColor(idea.idea_type)}20`,
                color: getTypeColor(idea.idea_type),
              }}>
                {idea.idea_type === IdeaType.NEW_FEATURE ? 'New Feature' : 'Enhancement'}
              </span>
              <span style={{
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: `${getStatusColor(idea.status)}20`,
                color: getStatusColor(idea.status),
              }}>
                {idea.status}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: theme.surfaceHover,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: theme.textSecondary,
              }}
            >
              &times;
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontWeight: '500', color: theme.text, display: 'block', marginBottom: '8px' }}>Description</label>
          {isEditing ? (
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '12px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                backgroundColor: theme.background,
                color: theme.text,
                resize: 'vertical',
                fontFamily: 'inherit',
                fontSize: '14px',
                lineHeight: '1.5',
              }}
            />
          ) : (
            <div style={{
              padding: '12px',
              backgroundColor: theme.surfaceHover,
              borderRadius: '6px',
              color: theme.text,
              whiteSpace: 'pre-wrap',
            }}>
              {idea.description}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={{ fontWeight: '500', color: theme.text, display: 'block', marginBottom: '4px' }}>Category</label>
            {isEditing ? (
              <select
                value={editCategory}
                onChange={(e) => {
                  setEditCategory(e.target.value);
                  setEditFeatureId('');
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  backgroundColor: theme.background,
                  color: theme.text,
                }}
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            ) : (
              <span style={{ color: theme.textSecondary }}>{idea.category}</span>
            )}
          </div>
          <div>
            <label style={{ fontWeight: '500', color: theme.text, display: 'block', marginBottom: '4px' }}>Created By</label>
            <span style={{ color: theme.textSecondary }}>{idea.created_by}</span>
          </div>
          {(idea.feature_id || (isEditing && idea.idea_type === IdeaType.FEATURE_ENHANCEMENT)) && (
            <div>
              <label style={{ fontWeight: '500', color: theme.text, display: 'block', marginBottom: '4px' }}>Feature</label>
              {isEditing ? (
                <select
                  value={editFeatureId}
                  onChange={(e) => setEditFeatureId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    backgroundColor: theme.background,
                    color: theme.text,
                  }}
                >
                  <option value="">Select a feature...</option>
                  {categoryFeatures.map(feature => (
                    <option key={feature.id} value={feature.id}>{feature.name}</option>
                  ))}
                </select>
              ) : (
                <span style={{ color: theme.textSecondary }}>{idea.feature_id}</span>
              )}
            </div>
          )}
          <div>
            <label style={{ fontWeight: '500', color: theme.text, display: 'block', marginBottom: '4px' }}>Suggested Team Member</label>
            {isEditing ? (
              <select
                value={editAiTeamMember}
                onChange={(e) => setEditAiTeamMember(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  backgroundColor: theme.background,
                  color: theme.text,
                }}
              >
                <option value="">Not specified</option>
                {teamMembers.map(member => (
                  <option key={member.name} value={member.name}>{member.name} - {member.role}</option>
                ))}
              </select>
            ) : (
              <span style={{ color: theme.textSecondary }}>{idea.ai_team_member || 'Not specified'}</span>
            )}
          </div>
        </div>

        {/* Edit mode buttons */}
        {isEditing && (
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <button
              onClick={handleCancelEdit}
              style={{
                padding: '10px 20px',
                backgroundColor: theme.surface,
                color: theme.text,
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={!editTitle.trim() || !editDescription.trim()}
              style={{
                padding: '10px 20px',
                backgroundColor: theme.primary,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                opacity: (!editTitle.trim() || !editDescription.trim()) ? 0.5 : 1,
              }}
            >
              Save Changes
            </button>
          </div>
        )}

        {/* Show what was created if accepted */}
        {idea.status === IdeaStatus.ACCEPTED && (
          <div style={{
            padding: '12px',
            backgroundColor: `${getStatusColor(IdeaStatus.ACCEPTED)}10`,
            borderRadius: '6px',
            marginBottom: '16px',
          }}>
            <span style={{ color: getStatusColor(IdeaStatus.ACCEPTED), fontWeight: '500' }}>
              {idea.created_feature_id && `Created feature: ${idea.created_feature_id}`}
              {idea.created_task_id && `Created task #${idea.created_task_id}`}
            </span>
            {idea.created_task_id && (
              <button
                onClick={() => idea.created_task_id && openTask(idea.created_task_id)}
                style={{
                  marginLeft: '12px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  backgroundColor: theme.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                View Task
              </button>
            )}
          </div>
        )}

        {/* Actions for proposed ideas */}
        {isProposed && !showAcceptTaskForm && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '20px' }}>
            {idea.idea_type === IdeaType.NEW_FEATURE && (
              <button
                onClick={() => onAcceptAsFeature(expectations)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#8b5cf6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Accept as Feature
              </button>
            )}
            <button
              onClick={() => setShowAcceptTaskForm(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
              }}
            >
              Accept as Task
            </button>
            <button
              onClick={onDefer}
              style={{
                padding: '10px 20px',
                backgroundColor: theme.surfaceHover,
                color: theme.text,
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Defer
            </button>
            <button
              onClick={onReject}
              style={{
                padding: '10px 20px',
                backgroundColor: '#ef444420',
                color: '#ef4444',
                border: '1px solid #ef4444',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Reject
            </button>
          </div>
        )}

        {/* Accept as Task form */}
        {showAcceptTaskForm && (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: theme.surfaceHover,
            borderRadius: '8px',
          }}>
            <h3 style={{ margin: '0 0 16px', color: theme.text }}>Accept as Task</h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: theme.text, fontWeight: '500' }}>Team Member *</label>
              <select
                value={selectedTeamMember}
                onChange={(e) => setSelectedTeamMember(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  backgroundColor: theme.background,
                  color: theme.text,
                }}
              >
                <option value="">Select team member...</option>
                {teamMembers.map(member => (
                  <option key={member.name} value={member.name}>{member.name} - {member.role}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: theme.text, fontWeight: '500' }}>Feature</label>
              <select
                value={selectedFeatureId}
                onChange={(e) => setSelectedFeatureId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  backgroundColor: theme.background,
                  color: theme.text,
                }}
              >
                <option value="">Select feature...</option>
                {features.filter(f => f.category === idea.category).map(feature => (
                  <option key={feature.id} value={feature.id}>{feature.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowAcceptTaskForm(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: theme.surface,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => onAcceptAsTask(selectedTeamMember, selectedFeatureId || undefined)}
                disabled={!selectedTeamMember}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: !selectedTeamMember ? 0.5 : 1,
                }}
              >
                Create Task
              </button>
            </div>
          </div>
        )}

        {/* Delete button */}
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${theme.border}` }}>
          <button
            onClick={onDelete}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: '#ef4444',
              border: '1px solid #ef4444',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Delete Idea
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Ideas Page
const Ideas: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [ideasData, setIdeasData] = useState<IdeasByCategory | null>(null);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<IdeaStatus | ''>('');

  const loadData = useCallback(async () => {
    try {
      const [ideasResult, featuresResult] = await Promise.all([
        getIdeasByCategory(),
        getFeatures(),
      ]);
      setIdeasData(ideasResult);
      setFeatures(featuresResult);
      // Expand categories that have ideas
      const catsWithIdeas = new Set<string>();
      Object.entries(ideasResult.ideas).forEach(([catId, ideas]) => {
        if (ideas.length > 0) catsWithIdeas.add(catId);
      });
      setExpandedCategories(catsWithIdeas);
    } catch (error) {
      console.error('Failed to load ideas:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateIdea = async (ideaData: IdeaCreate) => {
    try {
      await createIdea(ideaData);
      setShowCreateModal(false);
      loadData();
    } catch (error) {
      console.error('Failed to create idea:', error);
    }
  };

  const handleAcceptAsFeature = async (expectations?: string) => {
    if (!selectedIdea) return;
    try {
      await acceptIdeaAsFeature(selectedIdea.id, expectations);
      setSelectedIdea(null);
      loadData();
    } catch (error) {
      console.error('Failed to accept idea as feature:', error);
    }
  };

  const handleAcceptAsTask = async (aiTeamMember: string, featureId?: string) => {
    if (!selectedIdea) return;
    try {
      await acceptIdeaAsTask(selectedIdea.id, aiTeamMember, featureId);
      setSelectedIdea(null);
      loadData();
    } catch (error) {
      console.error('Failed to accept idea as task:', error);
    }
  };

  const handleReject = async () => {
    if (!selectedIdea) return;
    try {
      await rejectIdea(selectedIdea.id);
      setSelectedIdea(null);
      loadData();
    } catch (error) {
      console.error('Failed to reject idea:', error);
    }
  };

  const handleDefer = async () => {
    if (!selectedIdea) return;
    try {
      await deferIdea(selectedIdea.id);
      setSelectedIdea(null);
      loadData();
    } catch (error) {
      console.error('Failed to defer idea:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedIdea || !window.confirm('Are you sure you want to delete this idea?')) return;
    try {
      await deleteIdea(selectedIdea.id);
      setSelectedIdea(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete idea:', error);
    }
  };

  const handleUpdate = async (updates: { title?: string; description?: string; category?: string; feature_id?: string; ai_team_member?: string }) => {
    if (!selectedIdea) return;
    try {
      const updatedIdea = await updateIdea(selectedIdea.id, updates);
      setSelectedIdea(updatedIdea);
      loadData();
    } catch (error) {
      console.error('Failed to update idea:', error);
    }
  };

  const toggleCategory = (catId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(catId)) {
      newExpanded.delete(catId);
    } else {
      newExpanded.add(catId);
    }
    setExpandedCategories(newExpanded);
  };

  // Filter ideas by status
  const filterIdeas = (ideas: Idea[]) => {
    if (!statusFilter) return ideas;
    return ideas.filter(i => i.status === statusFilter);
  };

  // Count ideas by status
  const countByStatus = (ideas: Idea[]) => {
    const counts = { proposed: 0, accepted: 0, rejected: 0, deferred: 0 };
    ideas.forEach(i => {
      counts[i.status as keyof typeof counts]++;
    });
    return counts;
  };

  const allIdeas = ideasData ? Object.values(ideasData.ideas).flat() : [];
  const statusCounts = countByStatus(allIdeas);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: theme.textSecondary }}>
        Loading ideas...
      </div>
    );
  }

  // Heights used to position the action bar (navbar is already accounted for in Layout)
  const actionBarHeight = 56; // approximate height of this action bar

  return (
    <div style={{ backgroundColor: theme.background, paddingTop: `${actionBarHeight}px` }}>
      {/* Action Bar (fixed below main navbar) */}
      <div style={{
        position: 'fixed',
        top: '56px', // Below the fixed navbar
        left: 0,
        right: 0,
        zIndex: 200,
        padding: '12px 16px',
        backgroundColor: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
      }}>
        <h1 style={{ margin: 0, fontSize: '18px', color: theme.text }}>Ideas</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '8px 16px',
            backgroundColor: theme.primary,
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px',
          }}
        >
          + New Idea
        </button>
      </div>

      {/* Status filter */}
      <div className="status-filter" style={{
        padding: '12px 16px',
        backgroundColor: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
      }}>
        <span style={{ color: theme.textSecondary, fontSize: '13px' }}>Filter:</span>
        <button
          onClick={() => setStatusFilter('')}
          style={{
            padding: '6px 12px',
            borderRadius: '16px',
            border: 'none',
            backgroundColor: !statusFilter ? theme.primary : theme.surfaceHover,
            color: !statusFilter ? '#fff' : theme.text,
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          All ({allIdeas.length})
        </button>
        <button
          onClick={() => setStatusFilter(IdeaStatus.PROPOSED)}
          style={{
            padding: '6px 12px',
            borderRadius: '16px',
            border: 'none',
            backgroundColor: statusFilter === IdeaStatus.PROPOSED ? '#f59e0b' : theme.surfaceHover,
            color: statusFilter === IdeaStatus.PROPOSED ? '#fff' : theme.text,
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Proposed ({statusCounts.proposed})
        </button>
        <button
          onClick={() => setStatusFilter(IdeaStatus.ACCEPTED)}
          style={{
            padding: '6px 12px',
            borderRadius: '16px',
            border: 'none',
            backgroundColor: statusFilter === IdeaStatus.ACCEPTED ? '#10b981' : theme.surfaceHover,
            color: statusFilter === IdeaStatus.ACCEPTED ? '#fff' : theme.text,
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Accepted ({statusCounts.accepted})
        </button>
        <button
          onClick={() => setStatusFilter(IdeaStatus.DEFERRED)}
          style={{
            padding: '6px 12px',
            borderRadius: '16px',
            border: 'none',
            backgroundColor: statusFilter === IdeaStatus.DEFERRED ? '#6b7280' : theme.surfaceHover,
            color: statusFilter === IdeaStatus.DEFERRED ? '#fff' : theme.text,
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Deferred ({statusCounts.deferred})
        </button>
        <button
          onClick={() => setStatusFilter(IdeaStatus.REJECTED)}
          style={{
            padding: '6px 12px',
            borderRadius: '16px',
            border: 'none',
            backgroundColor: statusFilter === IdeaStatus.REJECTED ? '#ef4444' : theme.surfaceHover,
            color: statusFilter === IdeaStatus.REJECTED ? '#fff' : theme.text,
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Rejected ({statusCounts.rejected})
        </button>
      </div>

      {/* Ideas by category (grid like Features page) */}
      <div style={{ padding: '24px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px'
        }}>
          {(ideasData?.categories || []).map((category) => {
            const categoryIdeas = filterIdeas(ideasData!.ideas[category.id] || []);

            return (
              <div
                key={category.id}
                style={{
                  backgroundColor: theme.surface,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '12px',
                  padding: '16px',
                  minHeight: categoryIdeas.length === 0 ? '200px' : '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  justifyContent: categoryIdeas.length === 0 ? 'center' : 'flex-start'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: theme.text }}>{category.name}</div>
                    <div style={{ fontSize: '12px', color: theme.textSecondary }}>{categoryIdeas.length} ideas</div>
                  </div>
                  <div>
                    <button
                      onClick={() => toggleCategory(category.id)}
                      style={{
                        padding: '6px 10px',
                        fontSize: '13px',
                        backgroundColor: theme.surfaceHover,
                        color: theme.text,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      {expandedCategories.has(category.id) ? 'Collapse' : 'Expand'}
                    </button>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  overflow: 'auto',
                  maxHeight: '380px'
                }}>
                  {(expandedCategories.has(category.id) ? categoryIdeas : categoryIdeas.slice(0, 5)).map(idea => (
                    <IdeaCard key={idea.id} idea={idea} onClick={() => setSelectedIdea(idea)} />
                  ))}

                  {categoryIdeas.length === 0 && (
                    <div style={{ padding: '12px', backgroundColor: theme.surfaceHover, borderRadius: '8px', color: theme.textSecondary, fontStyle: 'italic', textAlign: 'center' }}>
                      No ideas in this category
                    </div>
                  )}
                </div>

                {categoryIdeas.length > 5 && !expandedCategories.has(category.id) && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      onClick={() => toggleCategory(category.id)}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', backgroundColor: theme.primary, color: '#fff', cursor: 'pointer' }}
                    >
                      View all
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && ideasData && (
        <CreateIdeaModal
          categories={ideasData.categories}
          features={features}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateIdea}
        />
      )}

      {/* Detail Modal */}
      {selectedIdea && ideasData && (
        <IdeaDetailModal
          idea={selectedIdea}
          features={features}
          categories={ideasData.categories}
          onClose={() => setSelectedIdea(null)}
          onAcceptAsFeature={handleAcceptAsFeature}
          onAcceptAsTask={handleAcceptAsTask}
          onReject={handleReject}
          onDefer={handleDefer}
          onDelete={handleDelete}
          onUpdate={handleUpdate}
        />
      )}

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .status-filter {
            padding: 8px 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Ideas;
